import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, Plus, UploadCloud } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DateRange } from "@/components/DateRange";
import { ExportButtons } from "@/components/ReportPanel";
import { DataTable } from "@/components/ReportView";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useMasters } from "@/hooks/useMasters";
import { EntryDialog } from "@/components/EntryDialog";
import { api, can, errMsg, inr, monthStartISO, todayISO } from "@/lib/api";
import { QUICK_ACTIONS, ENTRY_KINDS } from "@/lib/entries";
import { useAuth } from "@/context/AuthContext";

function Kpi({ title, value, sub, color, hint, testId, delay }) {
  return (
    <div className="relative bg-white border border-slate-200 rounded-xl p-5 overflow-hidden fade-up hover:shadow-md transition-shadow duration-200" style={{ animationDelay: `${delay}ms` }} data-testid={testId}>
      <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: color }} />
      <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{title}</div>
      <div className={`num font-bold text-2xl sm:text-[28px] mt-2 tracking-tight ${value < 0 ? "text-red-600" : "text-slate-900"}`} data-testid={`${testId}-value`}>{inr(value)}</div>
      <div className="text-xs text-slate-500 mt-1">{sub}</div>
      <div className="hint-tag mt-3">{hint}</div>
    </div>
  );
}

const MOVES = [
  ["vi_credit", "VI Credit"], ["vi_extra", "VI Extra Amount"], ["vi_dse", "Total DSE Balance Transfer"], ["dse_extra", "DSE Extra Amount"],
  ["gross_profit", "Gross Profit (VI extra − DSE extra)"], ["dse_recovery", "Total DSE Cash/UPI/Bank Recovery"],
  ["dse_cash", "DSE Cash Recovery"], ["dse_upi", "DSE UPI Recovery"],
  ["dse_bank", "DSE Bank Recovery"], ["fl_adv", "FL Advance"], ["fl_comm", "FL Commission"], ["fl_adj", "FL Adv Adjusted"],
  ["fl_rec", "FL Recovery"], ["fl_paid", "FL Comm Paid"],
];

function Recon({ rows }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-auto" data-testid="dashboard-reconciliation">
      <table className="dense-table">
        <thead><tr><th>Ledger</th><th className="text-right">Opening</th><th className="text-right">+ Increase</th><th className="text-right">− Decrease</th><th className="text-right">Closing</th><th>Check</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.ledger}>
              <td className="font-medium">{r.ledger}</td>
              <td className="num text-right">{inr(r.opening)}</td>
              <td className="num text-right">{r.increase == null ? "—" : inr(r.increase)}</td>
              <td className="num text-right">{r.decrease == null ? "—" : inr(r.decrease)}</td>
              <td className="num text-right font-semibold">{inr(r.closing)}</td>
              <td>{r.matches === false ? <AlertTriangle className="h-4 w-4 text-red-600" /> : <CheckCircle2 className="h-4 w-4 text-emerald-600" />}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Dashboard() {
  const { user, version } = useAuth();
  const nav = useNavigate();
  const [frm, setFrm] = useState(monthStartISO());
  const [to, setTo] = useState(todayISO());
  const [d, setD] = useState(null);
  const [kind, setKind] = useState(null);
  const [dist, setDist] = useState("__all");
  const [cmp, setCmp] = useState(null);
  const masters = useMasters();

  useEffect(() => {
    const p = { frm, to, ...(dist !== "__all" ? { distributor_id: dist } : {}) };
    api.get("/dashboard", { params: p }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
    if (user.role === "admin") api.get("/reports/distributor-summary", { params: { frm, to } }).then((r) => setCmp(r.data)).catch(() => {});
  }, [frm, to, dist, version, user]);

  const actions = QUICK_ACTIONS.filter((a) => can(user, ENTRY_KINDS[a.kind].perm));
  const k = d?.kpis;
  const full = can(user, "report:all");

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#E60000] font-semibold">Daily Business Report</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">DBR Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Closing balances as of <b className="num">{to}</b> · period totals for selected range</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <Select value={dist} onValueChange={setDist}>
            <SelectTrigger className="h-9 w-[200px]" data-testid="dashboard-distributor-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">Combined (all 3)</SelectItem>
              {masters.distributors.map((x) => <SelectItem key={x.code} value={x.code}>{x.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <DateRange frm={frm} to={to} onChange={(a, b) => { setFrm(a); setTo(b); }} testPrefix="dashboard-range" />
        </div>
      </div>

      {d?.pending_approvals > 0 && (
        <button onClick={() => nav("/approvals")} className="w-full text-left bg-orange-50 border border-orange-200 text-orange-800 rounded-xl px-4 py-3 text-sm" data-testid="dashboard-pending-approvals">
          {d.pending_approvals} reversal request(s) admin approval ke liye pending hain →
        </button>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {full && <Kpi title="VI Virtual Balance" value={k?.vi_balance} sub="Current closing balance" color="#E60000" hint="Opening + Credit − Debit" testId="kpi-vi-balance" delay={0} />}
        <Kpi title="DSE Outstanding" value={k?.dse_outstanding} sub="Total unrecovered balance" color="#D97706" hint="Diya balance − actual recovery" testId="kpi-dse-outstanding" delay={60} />
        <Kpi title="FL Advance Pending" value={k?.fl_pending} sub="After commission adjustment" color="#0284C7" hint="Advance − adjust − recovery" testId="kpi-fl-pending" delay={120} />
        {full && <Kpi title="Cash + Bank/UPI" value={k?.funds} color="#059669" hint="Actual available funds" testId="kpi-funds" delay={180}
          sub={k ? Object.entries(k.accounts).map(([a, v]) => `${a} ${inr(v)}`).join(" · ") : ""} />}
      </div>

      {full && d && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3" data-testid="dashboard-extra-kpis">
          {[["DSE Wallet Balance (advance)", "dse_wallet"], ["Retailer VTOP Due", "retailer_due"], ["Total Receivable (Humein Lena Hai)", "party_receivable"], ["Total Payable (Humein Dena Hai)", "party_payable"], ["Net Party Balance", "party_net"],
            ["Total Assets", "total_assets"], ["Total Liabilities", "total_liabilities"], ["Net Capital / Equity", "net_capital"],
            ["Total Razorpay Received", "rz_received"], ["Razorpay Allocated to DSEs", "rz_allocated"], ["Unallocated Razorpay Balance", "rz_unallocated"],
            ["Total Capital Invested", "capital"], ["Total Bank Payments to VI", "vi_bank"], ["VI Extra Amount", "vi_extra"],
            ["Total VI Payout Received", "payout_received"], ["VI Payout Pending", "payout_pending"], ["Total DSE Balance Transfer", "vi_dse"],
            ["DSE Extra Amount", "dse_extra"], ["Total Cash/UPI Recovery", "dse_recovery"], ["Gross Gain", "gross_profit"], ["Net Profit", "net_profit"]].filter(([, key]) => key in d.moves).map(([l, key]) => (
            <div key={key} className={`rounded-xl border px-4 py-3 ${["gross_profit", "net_profit"].includes(key) ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200"}`}>
              <div className={`text-[11px] uppercase tracking-wider font-semibold ${["gross_profit", "net_profit"].includes(key) ? "text-[#FFC000]" : "text-slate-500"}`}>{l}</div>
              <div className="num text-lg font-bold mt-1" data-testid={`kpi-${key}`}>{inr(d.moves[key] || 0)}</div>
            </div>
          ))}
        </div>
      )}

      {full && d?.party_outstanding?.length > 0 && (
        <section className="space-y-3" data-testid="dashboard-party-outstanding">
          <h2 className="font-display text-lg font-bold text-slate-900">Party-wise Outstanding</h2>
          <DataTable testId="party-outstanding-table" rows={d.party_outstanding}
            columns={[{ key: "party_id", label: "Party ID" }, { key: "party_name", label: "Party" }, { key: "distributor_id", label: "Distributor" },
              { key: "receivable", label: "Receivable", type: "money" }, { key: "payable", label: "Payable", type: "money" }, { key: "net", label: "Net", type: "money" }]} />
        </section>
      )}

      {cmp && (
        <section className="space-y-3" data-testid="dashboard-distributor-comparison">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h2 className="font-display text-lg font-bold text-slate-900">Account-wise Profit Comparison</h2>
            <ExportButtons path="/reports/distributor-summary" params={{ frm, to }} name="distributor-summary" />
          </div>
          <DataTable columns={cmp.columns} rows={cmp.rows} totals={cmp.totals} testId="distributor-summary-table" />
        </section>
      )}

      {actions.length > 0 && (
        <section>
          <h2 className="font-display text-lg font-bold text-slate-900 mb-3">Quick actions</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-2">
            {actions.map((a) => (
              <button key={a.kind} onClick={() => setKind(a.kind)} data-testid={`quick-${a.kind}`}
                className="group text-left bg-white border border-slate-200 rounded-lg px-3 py-3 hover:-translate-y-0.5 hover:shadow-sm transition-[transform,box-shadow] duration-150">
                <Plus className="h-4 w-4 mb-2" style={{ color: a.color }} />
                <span className="text-xs font-semibold text-slate-800 leading-tight block">{a.label}</span>
              </button>
            ))}
            {can(user, "import:preview") && (
              <button onClick={() => nav("/import")} data-testid="quick-import" className="text-left bg-slate-900 text-white rounded-lg px-3 py-3 hover:-translate-y-0.5 transition-transform duration-150">
                <UploadCloud className="h-4 w-4 mb-2 text-[#FFC000]" />
                <span className="text-xs font-semibold leading-tight block">Import Excel</span>
              </button>
            )}
          </div>
        </section>
      )}

      {full && <section className="grid grid-cols-1 xl:grid-cols-[1.3fr_1fr] gap-6">
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h2 className="font-display text-lg font-bold text-slate-900">Opening → Closing reconciliation</h2>
            {can(user, "report:all") && <ExportButtons path="/reports/daily-dbr" params={{ frm, to }} name="daily-dbr" />}
          </div>
          {d && <Recon rows={d.reconciliation} />}
          {d?.excess_dse?.length > 0 && (
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 text-sm" data-testid="dashboard-excess-dse">
              <div className="font-semibold text-orange-800 mb-1">Excess payment flagged (DSE credit balance)</div>
              {d.excess_dse.map((r) => <div key={r.dse_id} className="num text-orange-900">{r.dse_id} · {r.dse_name}: {inr(r.closing)}</div>)}
            </div>
          )}
        </div>
        <div>
          <h2 className="font-display text-lg font-bold text-slate-900 mb-3">Period totals</h2>
          <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100" data-testid="dashboard-period-totals">
            {MOVES.filter(([key]) => key !== "gross_profit" || (d && key in d.moves)).map(([key, label]) => (
              <div key={key} className="flex justify-between px-4 py-2 text-sm">
                <span className="text-slate-600">{label}</span>
                <span className="num font-semibold" data-testid={`period-${key}`}>{inr(d?.moves?.[key] || 0)}</span>
              </div>
            ))}
          </div>
        </div>
      </section>}

      {kind && <EntryDialog kind={kind} open={!!kind} onOpenChange={(o) => !o && setKind(null)} />}
    </div>
  );
}
